/**
 * Glow Kit home page behaviour.
 *
 * Two interactive pieces: the hero video and the before/after compare slider.
 * The slider's split is published as a --gk-split custom property; the CSS does
 * the clipping, so there is no width measurement to keep in sync.
 */

function initGlowKitHome(root) {
  const video = root.querySelector('.gk-hero-video');
  if (video) {
    video.muted = true;
    video.loop = false;
    const play = () => video.play().catch(() => {});
    play();
    video.addEventListener('loadeddata', play, { once: true });
    // Play through once, then ping-pong the closing second forever.
    video.addEventListener('ended', () => pingPongTail(video, root.querySelector('.gk-hero')), {
      once: true,
    });
  }

  setupDeferredVideo(root.querySelector('.gk-viz-video'), root.querySelector('.gk-viz'));
  setupDeferredVideo(
    root.querySelector('.gk-install-video'),
    root.querySelector('.gk-install'),
    '600px 0px'
  );

  const compare = root.querySelector('[data-gk-compare]');
  if (compare) {
    let dragging = false;

    const setFromEvent = (event) => {
      const rect = compare.getBoundingClientRect();
      if (!rect.width) return;
      const pct = Math.min(98, Math.max(2, ((event.clientX - rect.left) / rect.width) * 100));
      compare.style.setProperty('--gk-split', pct + '%');
    };

    compare.addEventListener('pointerdown', (event) => {
      dragging = true;
      setFromEvent(event);
    });
    compare.addEventListener('pointermove', (event) => {
      if (dragging) setFromEvent(event);
    });
    window.addEventListener('pointerup', () => {
      dragging = false;
    });
  }
}

function setupDeferredVideo(video, section, loadMargin = '0px') {
  if (!video || !section) return;

  video.muted = true;
  video.pause();

  const load = () => {
    const source = video.querySelector('source[data-src]');
    if (!source || source.src) return;
    source.src = source.dataset.src;
    source.removeAttribute('data-src');
    video.load();
  };

  if (!('IntersectionObserver' in window)) {
    load();
    video.play().catch(() => {});
    return;
  }

  new IntersectionObserver(
    ([entry], observer) => {
      if (!entry.isIntersecting) return;
      load();
      observer.disconnect();
    },
    { rootMargin: loadMargin }
  ).observe(section);

  new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        load();
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    },
    { threshold: 0.2 }
  ).observe(section);
}

/**
 * After the hero video finishes, oscillate over its last TAIL_SECONDS: run
 * backwards to (end - TAIL_SECONDS), forwards to the end, repeat.
 *
 * Only the forward leg can use native playback — HTML video has no reverse.
 * The backward leg has to seek frame by frame, and the ordering matters: a
 * seek issued while the previous one is still decoding replaces it rather
 * than queueing, so driving this from requestAnimationFrame collapses the
 * whole rewind into a single jump. Each seek is therefore issued from the
 * previous one's `seeked` event, and the target is derived from elapsed wall
 * time rather than a fixed frame step — that way the rewind always takes
 * TAIL_SECONDS to match the forward leg, dropping frames instead of running
 * in slow motion when the video's keyframes are sparse and seeks are slow.
 */
const TAIL_SECONDS = 0.8;

function pingPongTail(video, hero) {
  const end = video.duration;
  if (!isFinite(end) || end <= 0) return;
  const start = Math.max(0, end - TAIL_SECONDS);

  let rewindStartedAt = 0;
  let running = false;
  let onScreen = true;

  const onSeeked = () => {
    if (!running) return;
    const elapsed = (performance.now() - rewindStartedAt) / 1000;
    const next = end - elapsed;
    if (next <= start) {
      video.removeEventListener('seeked', onSeeked);
      video.currentTime = start;
      video.play().catch(() => {});
      return;
    }
    video.currentTime = next;
  };

  const rewind = () => {
    if (!running) return;
    video.pause();
    rewindStartedAt = performance.now();
    video.addEventListener('seeked', onSeeked);
    // Kick the chain off with a seek that is definitely a change from `end`,
    // otherwise the no-op seek never fires `seeked` and nothing follows it.
    video.currentTime = Math.max(start, end - 1 / 60);
  };

  /* The loop decodes a frame per seek and never settles, so it has to stop
     whenever nobody can see it — the hero scrolled past, or the tab hidden.
     Without this it keeps seeking for as long as the tab stays open, which on
     a page with five more sections below is most of the visit. */
  const sync = () => {
    const shouldRun = onScreen && !document.hidden;
    if (shouldRun === running) return;
    running = shouldRun;
    if (running) {
      rewind();
    } else {
      video.removeEventListener('seeked', onSeeked);
      video.pause();
    }
  };

  video.addEventListener('ended', rewind);
  document.addEventListener('visibilitychange', sync);
  if (hero && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      sync();
    }).observe(hero);
  }

  sync();
}

function initAll(scope) {
  (scope || document).querySelectorAll('.glow-kit-home').forEach(initGlowKitHome);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

// Theme editor re-renders the section in place.
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
